import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Campaign as CampaignIcon,
  Person as PersonIcon,
  ShoppingCart as ShoppingCartIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Link, useNavigate } from 'react-router-dom';
import { Notification, NotificationType } from '../types/notification';
import { useNotifications } from '../contexts/NotificationContext';
import NotificationExample from '../components/NotificationExample';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`notification-tabpanel-${index}`}
      aria-labelledby={`notification-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const NotificationsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  const {
    notifications,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
  } = useNotifications();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuNotificationId, setMenuNotificationId] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Filtrar notificações com base na pesquisa e tipo
  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = 
      notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || notification.type === filterType;
    
    return matchesSearch && matchesType;
  });
  
  // Abrir diálogo de confirmação
  const handleOpenDialog = (notification: Notification) => {
    setSelectedNotification(notification);
    setOpenDialog(true);
  };
  
  // Fechar diálogo
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedNotification(null);
  };
  
  // Abrir menu de opções
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuNotificationId(id);
  };
  
  // Fechar menu de opções
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuNotificationId(null);
  };
  
  // Obter ícone com base no tipo de notificação
  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'info':
        return <InfoIcon color="info" />;
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon />;
    }
  };
  
  // Obter cor com base no tipo de notificação
  const getNotificationColor = (type: NotificationType) => {
    switch (type) {
      case 'info':
        return 'info';
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Obter ícone com base no título da notificação
  const getNotificationCategoryIcon = (title: string) => {
    const lowerTitle = title.toLowerCase();
    
    if (lowerTitle.includes('campanha') || lowerTitle.includes('marketing')) {
      return <CampaignIcon />;
    } else if (lowerTitle.includes('lead') || lowerTitle.includes('cliente')) {
      return <PersonIcon />;
    } else if (lowerTitle.includes('produto') || lowerTitle.includes('venda')) {
      return <ShoppingCartIcon />;
    } else if (lowerTitle.includes('email')) {
      return <EmailIcon />;
    } else if (lowerTitle.includes('whatsapp')) {
      return <WhatsAppIcon />;
    } else {
      return <NotificationsIcon />;
    }
  };
  
  // Navegar para o link da notificação
  const handleNavigateToLink = (link?: string) => {
    if (link) {
      navigate(link);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1" display="flex" alignItems="center">
            <NotificationsIcon sx={{ mr: 1, color: 'primary.main' }} />
            Notificações
          </Typography>
          
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<DoneAllIcon />}
              onClick={markAllAsRead}
              disabled={notifications.length === 0 || notifications.every(n => n.read)}
            >
              Marcar todas como lidas
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={deleteAllNotifications}
              disabled={notifications.length === 0}
            >
              Limpar todas
            </Button>
          </Box>
        </Box>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="notification tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<NotificationsIcon />}
            label="Minhas Notificações"
            id="notification-tab-0"
            aria-controls="notification-tabpanel-0"
          />
          <Tab
            icon={<ScienceIcon />}
            label="Exemplo"
            id="notification-tab-1"
            aria-controls="notification-tabpanel-1"
          />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Pesquisar notificações..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="filter-type-label">Filtrar por tipo</InputLabel>
                <Select
                  labelId="filter-type-label"
                  value={filterType}
                  label="Filtrar por tipo"
                  onChange={(e: SelectChangeEvent) => setFilterType(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterListIcon />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="all">Todos os tipos</MenuItem>
                  <MenuItem value="info">Informação</MenuItem>
                  <MenuItem value="success">Sucesso</MenuItem>
                  <MenuItem value="warning">Aviso</MenuItem>
                  <MenuItem value="error">Erro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={12} md={4} display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchNotifications}
              >
                Atualizar
              </Button>
            </Grid>
          </Grid>
          
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 5 }}>
                <NotificationsIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Nenhuma notificação encontrada
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {searchTerm || filterType !== 'all' 
                    ? 'Tente ajustar seus filtros de pesquisa' 
                    : 'Você não tem notificações no momento'}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <List>
              {filteredNotifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      bgcolor: notification.read ? 'transparent' : 'action.hover',
                      '&:hover': {
                        bgcolor: 'action.selected',
                      },
                    }}
                  >
                    <ListItemIcon>
                      {getNotificationIcon(notification.type)}
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="subtitle1"
                            component="div"
                            sx={{ fontWeight: notification.read ? 'normal' : 'bold' }}
                          >
                            {notification.title}
                          </Typography>
                          <Chip
                            size="small"
                            label={notification.type}
                            color={getNotificationColor(notification.type)}
                            sx={{ ml: 1 }}
                          />
                          {getNotificationCategoryIcon(notification.title)}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{ mb: 1 }}
                          >
                            {notification.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(notification.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </Typography>
                          {notification.link && (
                            <Button
                              size="small"
                              sx={{ mt: 1 }}
                              onClick={() => handleNavigateToLink(notification.link)}
                            >
                              {notification.linkText || 'Ver detalhes'}
                            </Button>
                          )}
                        </Box>
                      }
                    />
                    
                    <ListItemSecondaryAction>
                      <Box display="flex" alignItems="center">
                        {!notification.read && (
                          <Tooltip title="Marcar como lida">
                            <IconButton
                              edge="end"
                              onClick={() => markAsRead(notification.id)}
                              sx={{ mr: 1 }}
                            >
                              <DoneAllIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="Opções">
                          <IconButton
                            edge="end"
                            onClick={(e) => handleOpenMenu(e, notification.id)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  
                  {index < filteredNotifications.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <NotificationExample />
        </TabPanel>
      </Paper>
      
      {/* Menu de opções */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => {
          if (menuNotificationId) {
            markAsRead(menuNotificationId);
          }
          handleCloseMenu();
        }}>
          <DoneAllIcon fontSize="small" sx={{ mr: 1 }} />
          Marcar como lida
        </MenuItem>
        <MenuItem onClick={() => {
          if (menuNotificationId) {
            const notification = notifications.find(n => n.id === menuNotificationId);
            if (notification) {
              handleOpenDialog(notification);
            }
          }
          handleCloseMenu();
        }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Excluir
        </MenuItem>
      </Menu>
      
      {/* Diálogo de confirmação */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
      >
        <DialogTitle>Excluir notificação</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir a notificação "{selectedNotification?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={() => selectedNotification && deleteNotification(selectedNotification.id)}
            color="error"
            autoFocus
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationsPage; 